// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Clickable from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { MappedLink } from "@stoneage/MobiusOverrides/Menu/Menu";
import { fontConfig } from "@stoneage/XDL/constants";
import { color, styledScrollbar } from "@stoneage/XDL/Utilities";
import { useId } from "@floating-ui/react";
import useMenuLinkClick from "@stoneage/MobiusOverrides/Menu/useMenuLinkClick";
import React, { FC, memo } from "react";
import styled, { css } from "styled-components";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";

export const WideMegaMenuStyle = styled.ul<{ referenceIndex?: number; currentDepth?: number }>`
    display: grid;
    grid-template-columns: 960px;
    background: ${getColor("common.white")};
    z-index: ${getProp("theme.zIndex.menu")};
    border: 1px solid ${color("common.ui6")};
    height: 100%;
    list-style: none;
    gap: 0;

    ${injectCss}
`;

export interface WideMegaMenuStyles {
    categoriesContainer?: InjectableCss;
    categoriesInnerContainer?: InjectableCss;
    categoryTitleWrapper?: InjectableCss;
    categoryLink?: LinkPresentationProps;
    childCategoryContainer?: InjectableCss;
    childCategoryInnerContainer?: TypographyPresentationProps;
    childCategoryTitle?: TypographyPresentationProps;
    childCategoryItemLink?: LinkPresentationProps;
}

export const styles: WideMegaMenuStyles = {
    categoriesContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            align-content: start;
            overflow-y: auto;
            overflow-x: hidden;
            ${styledScrollbar(4)}
        `,
    },
    categoriesInnerContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 20px;
            width: 100%;
            padding: 12px;
            &:first-child {
                position: relative;
                &:after {
                    content: "";
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 1px;
                    height: 100%;
                    background-color: ${color("common.border")};
                }
            }
        `,
    },
    categoryTitleWrapper: {
        css: css`
            padding-bottom: 8px;
            border-bottom: 1px solid ${getColor("common.border")};
            width: 100%;
        `,
    },
    categoryLink: {
        color: "secondary",
        typographyProps: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
        css: css`
            align-self: baseline;
        `,
    },
    childCategoryContainer: {
        css: css`
           display grid;
           grid-template-columns: repeat(2, minmax(0, 1fr));
           gap: 24px 40px;
        `,
    },
    childCategoryInnerContainer: {
        css: css`
            display: flex;
        `,
    },
    childCategoryTitle: {
        ...fontConfig.smallSingleLine,
        weight: fontConfig.weight.medium,
        color: "secondary",
        css: css`
            display: block;
        `,
    },
    childCategoryItemLink: {
        color: "secondary",
        typographyProps: {
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.medium,
            color: "secondary",
            css: css`
                display: block;
            `,
        },
    },
};

export interface RenderWideMegaMenuItemsProps {
    childMenuItems?: MappedLink[];
}

export const RenderWideMegaMenuItems: FC<RenderWideMegaMenuItemsProps> = memo(({ childMenuItems }) => {
    const id = useId();
    const handleMenuLinkClick = useMenuLinkClick();

    if (!childMenuItems) {
        return null;
    }

    const visibleItems = childMenuItems.filter(item => !item.excludeFromNavigation);

    const renderColumnItems = (items: MappedLink[]) =>
        items.map((item, colIndex) => {
            return (
                <StyledWrapper key={`${item.title}_${colIndex}`} {...styles.childCategoryInnerContainer}>
                    {item.isNonClickable || !item.url ? (
                        <Typography {...styles.childCategoryTitle}>{item.title}</Typography>
                    ) : (
                        <Link
                            href={item.url}
                            target={item.openInNewWindow ? "_blank" : ""}
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                item.clickableProps?.onClick?.(event);
                                handleMenuLinkClick(event, {
                                    href: item.url,
                                    openInNewWindow: item.openInNewWindow,
                                });
                            }}
                            aria-label={item.title}
                            {...styles.childCategoryItemLink}
                        >
                            {item.title}
                        </Link>
                    )}
                </StyledWrapper>
            );
        });

    return (
        <React.Fragment key={id}>
            <StyledWrapper {...styles.categoriesContainer}>
                {visibleItems.map((categoryItem, index) => {
                    return (
                        <StyledWrapper {...styles.categoriesInnerContainer} key={`col_${categoryItem.title}_${index}`}>
                            <StyledWrapper {...styles.categoryTitleWrapper}>
                                <Link
                                    href={categoryItem.url}
                                    target={categoryItem.openInNewWindow ? "_blank" : ""}
                                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                                        handleMenuLinkClick(event, {
                                            href: categoryItem.url,
                                            openInNewWindow: categoryItem.openInNewWindow,
                                        })
                                    }
                                    {...styles.categoryLink}
                                >
                                    {categoryItem.title}
                                </Link>
                            </StyledWrapper>
                            <StyledWrapper {...styles.childCategoryContainer}>
                                {renderColumnItems(categoryItem.children ?? [])}
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </React.Fragment>
    );
});
